/**
 * Nostr auth and publish/subscribe hooks.
 * Wraps @nostrify/react for login state and provides helpers for game events.
 *
 * Default relay: Ditto (wss://relay.ditto.pub)
 */

import { useNostrLogin } from "@nostrify/react/login";
import { useCallback, useMemo } from "react";
import type { NostrEvent } from "@nostrify/types";

/** Default relays — Ditto primary */
export const DEFAULT_RELAYS = [
  "wss://relay.ditto.pub",
  "wss://relay.dreamith.to",
  "wss://nos.lol",
];

/** Current logged-in user info */
export interface NostrUser {
  pubkey: string;
  profile?: {
    name?: string;
    display_name?: string;
    picture?: string;
    about?: string;
  };
}

/**
 * Hook for Nostr authentication state.
 */
export function useNostrUser() {
  const { logins, addLogin, removeLogin } = useNostrLogin();

  const activeLogin = logins[0] ?? null;

  const user: NostrUser | null = useMemo(() => {
    if (!activeLogin) return null;
    return { pubkey: activeLogin.pubkey };
  }, [activeLogin]);

  const login = useCallback(async (options: { type: "extension" | "nsec"; nsec?: string }) => {
    if (options.type === "extension") {
      const ext = (window as any).nostr;
      if (!ext) throw new Error("No NIP-07 extension found. Please install nos2x, Alby, or Flamingo.");
      const pubkey = await ext.getPublicKey();
      addLogin({
        id: `extension:${pubkey}`,
        type: "extension",
        pubkey,
        createdAt: new Date().toISOString(),
        data: null,
      }, true);
    }
  }, [addLogin]);

  const logout = useCallback(() => {
    if (activeLogin) removeLogin(activeLogin.id);
  }, [activeLogin, removeLogin]);

  return { user, isLoggedIn: !!user, login, logout };
}

/**
 * Hook for publishing Nostr events via NIP-07 extension.
 */
export function useNostrPublisher() {
  const publish = useCallback(async (event: Partial<NostrEvent>): Promise<NostrEvent | null> => {
    try {
      const ext = (window as any).nostr;
      if (!ext) { console.warn("No NIP-07 extension available"); return null; }

      const fullEvent: NostrEvent = {
        kind: event.kind ?? 1,
        content: event.content ?? "",
        tags: event.tags ?? [],
        created_at: event.created_at ?? Math.floor(Date.now() / 1000),
        pubkey: "",
        id: "",
        sig: "",
      };

      const signed = await ext.signEvent(fullEvent);

      // Publish to Ditto and fallback relays
      for (const url of DEFAULT_RELAYS) {
        try {
          const ws = new WebSocket(url);
          ws.onopen = () => { ws.send(JSON.stringify(["EVENT", signed])); setTimeout(() => ws.close(), 2000); };
        } catch { /* relay unreachable */ }
      }

      return signed;
    } catch (err) {
      console.error("Failed to publish event:", err);
      return null;
    }
  }, []);

  return { publish };
}

/**
 * Hook for subscribing to Nostr events.
 */
export function useNostrSubscription() {
  const subscribe = useCallback(
    (
      filters: Array<{ kinds?: number[]; authors?: string[]; [key: string]: unknown }>,
      onEvent: (event: NostrEvent) => void,
      relays?: string[],
    ): (() => void) => {
      const controllers: AbortController[] = [];
      const urls = relays ?? DEFAULT_RELAYS;

      for (const url of urls) {
        const ctrl = new AbortController();
        controllers.push(ctrl);
        try {
          const ws = new WebSocket(url);
          ws.onopen = () => ws.send(JSON.stringify(["REQ", `onegroup-${Date.now()}`, ...filters]));
          ws.onmessage = (msg) => {
            try {
              const data = JSON.parse(msg.data);
              if (data[0] === "EVENT" && data[2]) onEvent(data[2]);
            } catch { /* ignore */ }
          };
          ctrl.signal.addEventListener("abort", () => ws.close());
        } catch { /* ignore */ }
      }

      return () => controllers.forEach((c) => c.abort());
    },
    [],
  );

  return { subscribe };
}